import './mooring-select-entries-for-populate.scss';
import { DialogController } from 'aurelia-dialog';
import { autoinject, observable } from 'aurelia-framework';

export type TemplatePopulateSelection = {
  id: number | string;
  name: string;
  selected: boolean;
  articles: number;
  _hidden?: boolean;
};

export type SelectionOption = {
  id: number | string;
  name: string;
  selected: boolean;
  articles: number;
  meta?: string;
};

export type MooringSelectEntriesForPupulateArgs = {
  groups: {
    _identifier: string | number;
    title: string;
    options: SelectionOption[];
    collapsed?: boolean;
  }[];
};

export type MooringSelectEntriesForPupulateResult = {
  selections: TemplatePopulateSelection[];
};

@autoinject
export class MooringSelectEntriesForPupulateDialog {
  protected title: string = 'mooring.selectComponentsToPopulate';

  @observable
  protected filter: 'hide_with_articles' | 'show_all' = 'show_all';

  protected filterChanged() {
    if (!this.groups?.length) return;

    this.groups = this.groups?.map((group) => {
      const options = group.options?.map((option) => ({
        ...option,
        _hidden: this.filter === 'hide_with_articles' && option.articles > 0,
      }));
      const visible = options.filter((o) => !o._hidden);
      return {
        title: group.title,
        allSelected: visible.length > 0 && visible.every((o) => o.selected),
        collapsed: group.collapsed,
        options,
      };
    });
  }

  protected originalGroups: MooringSelectEntriesForPupulateArgs['groups'] = [];

  protected groups: {
    title: string;
    allSelected: boolean;
    collapsed: boolean;
    options: TemplatePopulateSelection[];
  }[] = [];

  constructor(private dialogController: DialogController) {}

  protected activate({ groups }: MooringSelectEntriesForPupulateArgs) {
    this.originalGroups = structuredClone(groups);
    this.groups = this.originalGroups?.map((group) => ({
      title: group.title,
      allSelected: false,
      collapsed: group.collapsed ?? false,
      options: group.options
        .filter((option) => {
          if (this.filter === 'hide_with_articles') {
            return option.articles === 0;
          }
          return true;
        })
        .map((option) => ({
          id: option.id,
          name: option.name,
          meta: option.meta,
          selected: false,
          articles: option.articles,
          _hidden: false,
        })),
    }));
  }

  protected selectAll() {
    this.clearSelections();

    for (const element of this.groups) {
      for (const option of element.options) {
        option.selected = true;
      }
      element.allSelected = true;
    }
  }

  protected clearSelections() {
    for (const element of this.groups) {
      element.options.forEach((option) => {
        option.selected = false;
      });
      element.allSelected = false;
    }
  }

  protected toggleCollapse(group: { collapsed: boolean }) {
    group.collapsed = !group.collapsed;
  }

  protected toggleGroup(group: { title: string; allSelected: boolean; options: TemplatePopulateSelection[] }) {
    const selected = group.allSelected ?? true;
    group.options = group.options.map((option) => {
      return {
        ...option,
        selected: option._hidden ? option.selected : selected,
      };
    });
    group.allSelected = selected;
  }

  protected onDone() {
    const selections = this.groups.flatMap((group) => group.options);
    void this.dialogController.cancel({ selections });
  }

  protected onCancel() {
    void this.dialogController.cancel();
  }
}
